// UI interaction handlers for tabs, preview HTML and incremental compile queue.
// Example: applyHtml() sanitizes preview markup before mounting it.
import { INCREMENTAL_COMPILE_DELAY_MS, elements, mutableState } from './state'
import { buildPreviewHtmlFragment } from './preview'
import { setStatus, updateLuisStats } from './render'
import { runCompile } from './compile'
import { setLineNumbers, syncLineNumberScroll } from './line-numbers'
import { playgroundMessages } from './messages'

// Keep gutter numbers in sync with the current Luis input line count.
export function updateLuisInputLineNumbers(): void {
  setLineNumbers(elements.luisLineNumbers, elements.luisInput.value)
  syncLineNumberScroll(elements.luisInput, elements.luisLineNumbers)
}

// Apply HTML editor content into preview mount area.
export function applyHtml(showStatus = true): void {
  const result = buildPreviewHtmlFragment(elements.htmlInput.value)
  elements.previewMount.replaceChildren(result.fragment)
  if (showStatus) {
    const wasSanitized = result.removedScriptCount > 0 || result.removedUnsafeAttributeCount > 0
    setStatus(
      wasSanitized ? playgroundMessages.previewHtmlUpdatedSanitized : playgroundMessages.previewHtmlUpdated,
      'ok',
    )
  }
}

// Queue delayed compile for line-break edits to keep typing smooth.
export function queueIncrementalCompile(): void {
  if (mutableState.incrementalCompileTimer !== null) {
    window.clearTimeout(mutableState.incrementalCompileTimer)
  }

  mutableState.incrementalCompileTimer = window.setTimeout(() => {
    mutableState.incrementalCompileTimer = null
    runCompile(false)
  }, INCREMENTAL_COMPILE_DELAY_MS)
}

// Handle luis input event and trigger compile/update logic.
export function handleLuisInput(): void {
  updateLuisInputLineNumbers()
  updateLuisStats(elements.luisInput.value)
  if (mutableState.pendingInputType === 'insertLineBreak' || mutableState.pendingInputType === 'insertParagraph') {
    queueIncrementalCompile()
    mutableState.pendingInputType = null
    return
  }

  mutableState.pendingInputType = null
  if (mutableState.incrementalCompileTimer !== null) {
    window.clearTimeout(mutableState.incrementalCompileTimer)
    mutableState.incrementalCompileTimer = null
  }
  runCompile(false)
}

// Toggle editor tabs between .luis source and HTML source.
export function setInputTab(tab: 'luis' | 'html'): void {
  const showLuis = tab === 'luis'
  elements.inputTabLuis.classList.toggle('is-active', showLuis)
  elements.inputTabHtml.classList.toggle('is-active', !showLuis)
  elements.inputTabLuis.setAttribute('aria-selected', String(showLuis))
  elements.inputTabHtml.setAttribute('aria-selected', String(!showLuis))
  elements.inputPanelLuis.classList.toggle('is-active', showLuis)
  elements.inputPanelHtml.classList.toggle('is-active', !showLuis)
}
